/**
 * HiringPipelineBoard — the hiring pipeline as an ADAPTER over the canonical
 * `Board` (UI/UX Patterns PRD Issue 11 follow-up; sibling of the CRM reference
 * adapter `CrmPipelineBoard`).
 *
 * Faithful, drag-preserving replacement for the legacy `PipelineKanban` fork:
 * same external contract (`stages` / `byStage` / `rejectedStage` / `rejected` /
 * `basePath`), but the kanban engine — dnd-kit drag, DragOverlay, optimistic
 * placement with revert, horizontal column-scroll + edge-fade — now lives ONCE
 * in `Board`. Everything hiring-specific rides on adapter props:
 *   • columns    ← active stages in order + the REJECTED terminal stage last,
 *                    each carrying `HIRING_STAGE_INTENT[kind]` + a per-stage
 *                    kind-label badge.
 *   • renderCard ← the application tile (candidate name · email · days-in-stage
 *                    stall indicator · scorecard-rating badge).
 *   • getItemId / onMove ← the numeric application id ↔ stage id.
 *
 * TWO move semantics are preserved through the single `onMove(appId, toStageId)`
 * seam: a move onto the rejected stage → the `reject` endpoint; any other move
 * → the `advance` endpoint. Board applies the move optimistically and reverts if
 * the persist promise rejects, so this adapter keeps NO local placement state
 * (the legacy fork's optimistic reconciliation is subsumed by Board's).
 *
 * No new board is forked here; this file only shapes hiring data for `Board`.
 */

import * as React from "react";
import { useNavigate as useRouter } from "../../router";
import { useTransition } from "react";
import { apiCall } from "../../api/client";
import {
  hiringApplications,
  type ApplicationWithJoins,
} from "../../api/contracts/hiring-applications";
import type {
  StageShape,
  StageKind,
} from "../../api/contracts/hiring-pipelines";
import { Board, type BoardColumn } from "../board";
import { useNavigateOrPeek } from "../object-link";
import { HIRING_STAGE_INTENT } from "../../tokens";
import { StatusBadge } from "../status-badge";
import { objectHref } from "../../path-label";
import { cn } from "../../utils";
import type { z } from "zod";
import type { overallRating } from "../../api/contracts/hiring-scorecards";

type ScorecardRating = z.infer<typeof overallRating>;

/** Days a candidate can sit in one stage before the card flags a stall. */
const STALL_DAYS = 14;

interface Props {
  /** Active stages in pipeline order (excludes rejected). */
  stages: StageShape[];
  /** All applications keyed by stage_id. */
  byStage: Record<number, ApplicationWithJoins[]>;
  /** Rejected stage id, if any (for the reject dropzone). */
  rejectedStage: StageShape | null;
  /** Rejected-stage applications. */
  rejected: ApplicationWithJoins[];
  /** Base path for application links. Defaults to "/hiring". */
  basePath?: string;
}

/** The scope-safe object page for an application (opened via Board's onOpen). */
function applicationHref(app: ApplicationWithJoins, basePath: string): string {
  // Open the scope-free object page when the row carries its synced uid (the
  // no-404 path: `${basePath}/applications/<id>` 404s at the modes workspace
  // tier). Fall back to the section route otherwise.
  return app.uid
    ? objectHref(app.uid, "operations")
    : `${basePath}/applications/${app.id}`;
}

/**
 * The application tile — shared by the resting card and the drag overlay. Pure
 * display: candidate name, email, a days-in-stage stall indicator, and the
 * latest scorecard rating. Board owns the clickable/draggable shell (click →
 * `onOpen` → the object page); nesting an `<a>` here would be invalid inside
 * Board's card `<button>`.
 */
function ApplicationTile({ app }: { app: ApplicationWithJoins }) {
  const stalled = app.days_in_stage >= STALL_DAYS;
  return (
    <div className="flex flex-col gap-1 type-body">
      <span className="text-foreground">{app.person_display_name}</span>
      <span className="truncate text-muted-foreground">
        {app.primary_email ?? "no email"}
      </span>
      <span className="mt-1 flex items-center justify-between gap-2">
        <span
          className={cn(
            "type-subtext tabular-nums",
            stalled ? "text-warning-fg" : "text-muted-foreground",
          )}
          title={stalled ? `Stalled — ${app.days_in_stage}d in stage` : undefined}
        >
          {app.days_in_stage}d in stage
        </span>
        {app.latest_scorecard_rating ? (
          <StatusBadge
            domain="scorecard_rating"
            status={app.latest_scorecard_rating as ScorecardRating}
          />
        ) : null}
      </span>
    </div>
  );
}

export function HiringPipelineBoard({
  stages,
  byStage,
  rejectedStage,
  rejected,
  basePath = "/hiring",
}: Props) {
  const router = useRouter();
  // A card opens the application's peek in place; the section-route fallback
  // (pre-uid rows) still navigates.
  const go = useNavigateOrPeek();
  const [, startTransition] = useTransition();

  const columns = React.useMemo<BoardColumn<ApplicationWithJoins>[]>(() => {
    const active: BoardColumn<ApplicationWithJoins>[] = stages.map((stage) => ({
      id: stage.id,
      name: stage.name,
      items: byStage[stage.id] ?? [],
      intent: HIRING_STAGE_INTENT[stage.kind as StageKind] ?? "neutral",
      badge: stage.kind,
      emptyLabel: "Drop candidates here.",
    }));
    if (rejectedStage) {
      active.push({
        id: rejectedStage.id,
        name: rejectedStage.name,
        items: byStage[rejectedStage.id] ?? rejected,
        intent: HIRING_STAGE_INTENT[rejectedStage.kind as StageKind] ?? "danger",
        badge: rejectedStage.kind,
        emptyLabel: "Drop a candidate here to reject.",
      });
    }
    return active;
  }, [stages, byStage, rejectedStage, rejected]);

  // The single move seam: rejected stage → the reject endpoint; any other stage
  // → the advance endpoint. Board owns the optimistic placement + revert, so a
  // thrown apiCall propagates back to Board (which reverts) — do NOT swallow it.
  const handleMove = React.useCallback(
    (applicationId: number, toStageId: number) =>
      new Promise<void>((resolve, reject) => {
        startTransition(async () => {
          try {
            if (rejectedStage && toStageId === rejectedStage.id) {
              await apiCall(hiringApplications.reject, {
                params: { id: applicationId },
                body: { reason: "moved to rejected via kanban" },
              });
            } else {
              await apiCall(hiringApplications.advance, {
                params: { id: applicationId },
                body: { to_stage_id: toStageId },
              });
            }
            router.refresh();
            resolve();
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        });
      }),
    [rejectedStage, router],
  );

  return (
    <Board<ApplicationWithJoins>
      dndId="hiring-pipeline-board"
      columns={columns}
      getItemId={(a) => a.id}
      onMove={(itemId, toColumnId) =>
        handleMove(Number(itemId), Number(toColumnId))
      }
      onOpen={(app) => go(applicationHref(app, basePath))}
      renderCard={(app) => <ApplicationTile app={app} />}
    />
  );
}
